import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import {
  ConsultationDischargeWriteDisabledError,
} from '../../domain/models/consultation-discharge-write-disabled.error';
import { Patient } from '../../domain/models/patient.model';
import { PatientListQuery } from '../../domain/models/patient-list-query.model';
import { PatientListResult } from '../../domain/models/patient-list-result.model';
import { PatientUpdateInput } from '../../domain/models/patient-update-input.model';
import { PatientTutorRef } from '../../domain/models/patient-tutor-ref.model';
import { PatientMetrics } from '../../domain/models/patient-metrics.model';
import {
  ConsultationAttachmentFile, ConsultationDischarge, ConsultationSectionDataMap,
  ConsultationSectionKey, Deworming, Vaccination,
} from '../../domain/models/clinical-history.model';
import {
  IPatientsRepository, PatientConsultationSummariesResult, PatientInvoicesResult,
} from '../../domain/repositories/patients.repository';
import { PatientsRemoteDatasource } from '../datasources/remote/patients.remote-datasource';
import { PatientMapper } from '../mappers/patient.mapper';
import { ClinicalHistoryMapper } from '../mappers/clinical-history.mapper';
import { InvoiceMapper } from '../mappers/invoice.mapper';
import { DepartmentTransferResult } from '@features/shared/departments/domain/models/department-transfer-result.model';
import { toDepartmentTransferResult } from '@features/shared/departments/data/mappers/department-transfer-result.mapper';

@Injectable()
export class PatientsRepositoryImpl implements IPatientsRepository {
  private readonly datasource = inject(PatientsRemoteDatasource);
  private readonly mapper = inject(PatientMapper);
  private readonly clinicalHistoryMapper = inject(ClinicalHistoryMapper);
  private readonly invoiceMapper = inject(InvoiceMapper);

  public list(query: PatientListQuery): Observable<PatientListResult> {
    const source$ = query.search
      ? this.datasource.search(
        query.search, query.page, query.pageSize, query.sortBy, query.sortDir, query.filters, query.departmentId,
      )
      : this.datasource.list(
        query.page, query.pageSize, query.sortBy, query.sortDir, query.filters, query.departmentId,
      );

    return source$.pipe(
      map(dto => ({
        items: dto.Items.map(item => this.mapper.toPatient(item)),
        total: dto.TotalCount,
        asOfUtc: dto.AsOfUtc ?? null,
      })),
    );
  }

  public getById(id: string): Observable<Patient> {
    return this.datasource.getById(id).pipe(map(dto => this.mapper.toPatient(dto)));
  }

  public getMetrics(departmentId?: number, archived?: 'true' | 'false'): Observable<PatientMetrics> {
    return this.datasource.getMetrics(departmentId, archived).pipe(map(dto => this.mapper.toPatientMetrics(dto)));
  }

  public create(input: PatientUpdateInput, departmentId?: number): Observable<Patient> {
    const payload = this.mapper.toCreatePayload(input);
    return this.datasource.create(payload, departmentId).pipe(map(dto => this.mapper.toPatient(dto)));
  }

  public update(id: string, changes: PatientUpdateInput): Observable<Patient> {
    const payload = this.mapper.toUpdatePayload(changes);
    return this.datasource.update(id, payload).pipe(map(dto => this.mapper.toPatient(dto)));
  }

  public delete(id: string): Observable<void> {
    return this.datasource.delete(id);
  }

  public moveToDepartment(id: string, departmentId: number): Observable<DepartmentTransferResult> {
    return this.datasource.moveToDepartment(id, departmentId).pipe(map(dto => toDepartmentTransferResult(dto)));
  }

  public recordWeight(patientId: string, weightKg: number): Observable<void> {
    return this.datasource
      .createWeightRecord(patientId, weightKg, new Date())
      .pipe(map(() => undefined));
  }

  public uploadPicture(id: string, file: File): Observable<Patient> {
    return this.datasource.uploadPicture(id, file).pipe(map(dto => this.mapper.toPatient(dto)));
  }

  public getTutor(id: string): Observable<PatientTutorRef> {
    return this.datasource.getTutor(id).pipe(map(dto => this.mapper.toTutorRef(dto)));
  }

  // Seeds every requested id with null upfront so ids the backend omits (not found) still resolve
  // to a definite "unknown" entry instead of being absent from the map (mirrors
  // AppointmentClientLookupRepositoryImpl.getByIds).
  public getTutorsByIds(ids: readonly string[]): Observable<ReadonlyMap<string, PatientTutorRef | null>> {
    return this.datasource.getTutorsByIds(ids).pipe(
      map((dto) => {
        const result = new Map<string, PatientTutorRef | null>(ids.map((id) => [id, null]));
        for (const item of dto.Items) {
          const tutorRef = this.mapper.toTutorRef(item);
          result.set(tutorRef.id, tutorRef);
        }
        return result;
      }),
      catchError(() => of(new Map(ids.map((id) => [id, null])))),
    );
  }

  public searchTutors(term: string): Observable<PatientTutorRef[]> {
    return this.datasource.searchTutors(term).pipe(
      map(dto => dto.Items.map(item => this.mapper.toTutorRef(item))),
    );
  }

  public getVaccinations(patientId: string): Observable<Vaccination[]> {
    return this.datasource.getVaccinations(patientId).pipe(
      map(items => items.map(item => this.clinicalHistoryMapper.toVaccination(item))),
    );
  }

  public getDewormings(patientId: string): Observable<Deworming[]> {
    return this.datasource.getDewormings(patientId).pipe(
      map(items => items.map(item => this.clinicalHistoryMapper.toDeworming(item))),
    );
  }

  public getConsultationSummaries(
    patientId: string, organizationId: number, page: number, pageSize: number,
  ): Observable<PatientConsultationSummariesResult> {
    return this.datasource.getConsultationSummaries(patientId, organizationId, page, pageSize).pipe(
      map(dto => ({
        items: dto.Items.map(item => this.clinicalHistoryMapper.toConsultationSummary(item)),
        total: dto.TotalCount,
      })),
    );
  }

  public getConsultationSection<K extends ConsultationSectionKey>(
    patientId: string, consultationId: string, section: K,
  ): Observable<ConsultationSectionDataMap[K]> {
    return this.datasource.getConsultationSection(patientId, consultationId, section).pipe(
      map(dto => this.clinicalHistoryMapper.toConsultationSection(section, dto)),
    );
  }

  public saveConsultationDischarge(
    patientId: string, consultationId: string, html: string,
  ): Observable<ConsultationDischarge> {
    return this.datasource.saveConsultationDischarge(patientId, consultationId, html).pipe(
      map(dto => this.clinicalHistoryMapper.toConsultationDischarge(dto)),
      catchError((error: unknown) => throwError(() => (
        isDischargeWriteDisabled(error) ? new ConsultationDischargeWriteDisabledError() : error
      ))),
    );
  }

  public getInvoices(patientId: string, page: number, pageSize: number): Observable<PatientInvoicesResult> {
    return this.datasource.getInvoices(patientId, page, pageSize).pipe(
      map(dto => ({
        items: dto.Items.map(item => this.invoiceMapper.toInvoice(item)),
        total: dto.TotalCount,
      })),
    );
  }

  public getInvoicePdf(invoiceId: string): Observable<Blob> {
    return this.datasource.getInvoicePdf(invoiceId);
  }

  public downloadConsultationAttachment(
    patientId: string, consultationId: string, noteId: string, uploadId: string,
  ): Observable<Blob> {
    return this.datasource.downloadConsultationAttachment(patientId, consultationId, noteId, uploadId);
  }

  public getConsultationAnalysisReferralAttachments(
    patientId: string, consultationId: string, referralId: string,
  ): Observable<readonly ConsultationAttachmentFile[]> {
    return this.datasource.getConsultationAnalysisReferralAttachments(patientId, consultationId, referralId).pipe(
      map(dtos => this.clinicalHistoryMapper.toConsultationAttachmentFiles(dtos)),
    );
  }
}

// The backend answers the disabled write with a ProblemDetails naming its own configuration key.
// Matched against the WHOLE body rather than one field: which member carries it (`detail`, `code`,
// an extension) is the backend's choice, and the key name is unique enough that a substring match
// cannot collide with the role-based 403 the same route can also answer.
const DISCHARGE_WRITE_FEATURE_KEY = 'Features:ConsultationDischargeWrite';

function isDischargeWriteDisabled(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 403) return false;

  const body: unknown = error.error;
  const serialized = typeof body === 'string' ? body : JSON.stringify(body ?? '');
  return serialized.includes(DISCHARGE_WRITE_FEATURE_KEY);
}
